import { useState } from 'react'
import { Button, HStack, IconButton, Menu, Portal, Text, VStack } from '@chakra-ui/react'
import { FiCheck, FiMoreHorizontal } from 'react-icons/fi'
import { RESULT_STATUS_LABELS, ResultStatus } from '../../../types/script'
import type { TurfContactDto } from '../../../types/canvasser'
import { ConfirmDeceased } from './ConfirmDeceased'

export type PersonAction = 'moved' | 'deceased' | 'doesntLiveHere' | 'doesntWorkHere' | 'notAtWork'

const fullName = (c: TurfContactDto) => `${c.firstName} ${c.lastName ?? ''}`.trim()

/** [P4-3] "Not home · Oct 3" — this campaign's last attempt. No age until C1's field policy. */
export function lastVisitHint(c: TurfContactDto): string | null {
  if (!c.lastAttemptResult) return null
  const label = RESULT_STATUS_LABELS[c.lastAttemptResult] ?? c.lastAttemptResult
  const date = c.lastAttemptedAt
    ? new Date(c.lastAttemptedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null
  return date ? `${label} · ${date}` : label
}

function menuFor(variant: 'home' | 'worksite', allowed: ReadonlySet<ResultStatus>): { value: PersonAction; label: string }[] {
  const moved = allowed.has(ResultStatus.Moved)
  if (variant === 'worksite') {
    return [
      { value: 'notAtWork', label: 'Not at work today' },
      ...(moved ? [{ value: 'doesntWorkHere' as const, label: "Doesn't work here" }] : []),
    ]
  }
  return [
    ...(moved ? [{ value: 'moved' as const, label: 'Moved' }] : []),
    ...(allowed.has(ResultStatus.Deceased) ? [{ value: 'deceased' as const, label: 'Deceased' }] : []),
    ...(moved ? [{ value: 'doesntLiveHere' as const, label: "Doesn't live here" }] : []),
  ]
}

/** [Spec §4.5, §15.1 D2/D3] Who answered? (household) / Who did you find? (worksite). */
export function WhoAnsweredStep({
  variant, residents, handled, allowed, onStart, onRefused, onLanguageBarrier, onPersonAction, onNotOnList,
  finishLabel, onFinish, isSaving,
}: {
  variant: 'home' | 'worksite'
  residents: TurfContactDto[]
  handled: ReadonlyMap<string, string>
  allowed: ReadonlySet<ResultStatus>
  onStart: (ids: string[]) => void
  onRefused: (ids: string[]) => void
  onLanguageBarrier: (ids: string[]) => void
  onPersonAction: (contactId: string, action: PersonAction) => void
  onNotOnList: () => void
  finishLabel: string | null
  onFinish: () => void
  isSaving: boolean
}) {
  const [selected, setSelected] = useState<string[]>([])
  const [confirmDeceased, setConfirmDeceased] = useState<TurfContactDto | null>(null)
  const live = selected.filter(id => !handled.has(id))
  const names = residents.filter(r => live.includes(r.contactId)).map(r => r.firstName).join(', ')
  const toggle = (id: string) => setSelected(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]))
  const items = menuFor(variant, allowed)
  const act = (r: TurfContactDto, a: PersonAction) => {
    if (a === 'deceased') { setConfirmDeceased(r); return }
    setSelected(s => s.filter(x => x !== r.contactId))
    onPersonAction(r.contactId, a)
  }

  return (
    <VStack flex={1} align="stretch" gap={3} px={4} pb={6} minH={0}>
      <Text fontSize="xl" fontWeight="bold">{variant === 'worksite' ? 'Who did you find?' : 'Who answered?'}</Text>
      <VStack align="stretch" gap={2} overflowY="auto" flex={1}>
        {residents.map(r => {
          const done = handled.get(r.contactId)
          const on = live.includes(r.contactId)
          const sub = variant === 'worksite'
            ? [r.workDepartment, r.workPosition].filter(Boolean).join(' · ') || null
            : lastVisitHint(r)
          return (
            <HStack key={r.contactId} gap={2}>
              <Button flex={1} minH="56px" justifyContent="flex-start" textAlign="left" variant="outline"
                aria-pressed={on} disabled={!!done} borderColor={on ? 'accent' : 'border'} bg={on ? 'accent.soft' : 'surface'}
                onClick={() => toggle(r.contactId)}>
                <VStack align="flex-start" gap={0} flex={1}>
                  <Text fontWeight="semibold" color="fg">{fullName(r)}</Text>
                  {sub && <Text fontSize="sm" color="fg.muted">{sub}</Text>}
                </VStack>
                {on && <FiCheck aria-hidden />}
                {done && <Text fontSize="sm" color="fg.muted">{done}</Text>}
              </Button>
              {!done && items.length > 0 && (
                <Menu.Root positioning={{ placement: 'bottom-end' }}>
                  <Menu.Trigger asChild>
                    <IconButton aria-label={`More for ${fullName(r)}`} variant="ghost" minW="44px" minH="44px">
                      <FiMoreHorizontal />
                    </IconButton>
                  </Menu.Trigger>
                  <Portal>
                    <Menu.Positioner>
                      <Menu.Content>
                        {items.map(i => (
                          <Menu.Item key={i.value} value={i.value} minH="44px" onSelect={() => act(r, i.value)}>{i.label}</Menu.Item>
                        ))}
                      </Menu.Content>
                    </Menu.Positioner>
                  </Portal>
                </Menu.Root>
              )}
            </HStack>
          )
        })}
      </VStack>

      {confirmDeceased && (
        <ConfirmDeceased name={fullName(confirmDeceased)} onCancel={() => setConfirmDeceased(null)} onConfirm={() => {
          const id = confirmDeceased.contactId
          setConfirmDeceased(null)
          setSelected(s => s.filter(x => x !== id))
          onPersonAction(id, 'deceased')
        }} />
      )}

      <Button variant="ghost" minH="44px" alignSelf="flex-start" onClick={onNotOnList}>Someone not on my list</Button>
      <Button minH="56px" bg="accent" color="accent.fg" disabled={live.length === 0} onClick={() => onStart(live)}>
        {live.length === 0 ? 'Start conversation' : `Start conversation · ${names}`}
      </Button>
      <HStack gap={2}>
        {allowed.has(ResultStatus.Refused) && (
          <Button flex={1} minH="48px" variant="outline" borderColor="border" disabled={live.length === 0}
            onClick={() => onRefused(live)}>Refused at door</Button>
        )}
        {allowed.has(ResultStatus.LanguageBarrier) && (
          <Button flex={1} minH="48px" variant="outline" borderColor="border" disabled={live.length === 0}
            onClick={() => onLanguageBarrier(live)}>Language barrier</Button>
        )}
      </HStack>
      {finishLabel && (
        <Button minH="52px" variant="outline" borderColor="accent" color="accent" disabled={isSaving} onClick={onFinish}>
          {finishLabel}
        </Button>
      )}
    </VStack>
  )
}
